const normalizeUnitCode = (code) => String(code || "").trim().toUpperCase();

const getSkuCodes = (skuItem) =>
  [skuItem?.sku?.code, skuItem?.skuCode, skuItem?.stockKeepingUnitCode].filter(
    Boolean
  );

/**
 * Khớp đơn vị HIS (usedUnit) với SKU Store.
 * Ưu tiên mapping theo mã — tên lệch encoding/khác nhau không ảnh hưởng.
 */
const skuMatchesUnit = (skuItem, usedUnit) => {
  if (!skuItem || !usedUnit) return false;

  if (usedUnit.code) {
    const unitCode = normalizeUnitCode(usedUnit.code);
    if (
      getSkuCodes(skuItem).some((code) => normalizeUnitCode(code) === unitCode)
    ) {
      return true;
    }
  }

  if (usedUnit.id) {
    if (skuItem.skuId === usedUnit.id || skuItem.sku?.id === usedUnit.id) {
      return true;
    }
  }

  // Fallback theo tên — chỉ khi chưa map được qua mã/id
  if (usedUnit.name) {
    const unitName = usedUnit.name.trim().toLowerCase();
    const skuName = (skuItem.skuName || skuItem.sku?.name || "")
      .trim()
      .toLowerCase();
    if (skuName && skuName === unitName) {
      return true;
    }
  }

  return false;
};

const normalizeSkuName = (name) => String(name || "").trim().toLowerCase();

/** usedUnit trùng đơn vị nhỏ nhất (minSku) của SP trên Store. */
const usedUnitMatchesMinSku = (availabilityData, usedUnit) => {
  if (!availabilityData || !usedUnit) return false;

  // Ưu tiên khớp theo MÃ đơn vị (mã HIS = mã Store — luôn giống nhau).
  if (
    availabilityData.minSkuCode &&
    usedUnit.code &&
    normalizeUnitCode(availabilityData.minSkuCode) === normalizeUnitCode(usedUnit.code)
  ) {
    return true;
  }

  if (
    availabilityData.minSkuId &&
    usedUnit.id &&
    availabilityData.minSkuId === usedUnit.id
  ) {
    return true;
  }

  if (availabilityData.minSkuName && usedUnit.name) {
    if (
      normalizeSkuName(usedUnit.name) ===
      normalizeSkuName(availabilityData.minSkuName)
    ) {
      return true;
    }
  }

  const rows = availabilityData.rows || [];
  const matchedRows = rows.filter((row) => skuMatchesUnit(row, usedUnit));
  if (!matchedRows.length || !availabilityData.minSkuId) return false;

  return matchedRows.some(
    (row) =>
      row.skuId === availabilityData.minSkuId &&
      (row.coefficient == null || row.coefficient === 1)
  );
};

const getAvailableMinSku = (availabilityData) =>
  Math.max(
    0,
    (availabilityData?.totalMinSku || 0) - (availabilityData?.reservedMinSku || 0)
  );

/** Làm tròn số lượng tồn — tránh 692.0000000001 từ float. */
const roundInventoryQty = (qty) => {
  const n = Number(qty);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 10000) / 10000;
};

/**
 * Lấy tồn kho theo đơn vị đăng ký/xuất bán (usedUnit), không dùng đơn vị nhỏ nhất.
 * API cũ check-inventory + checkSku: true → mảng skus[].
 */
export const getInventoryQuantityByUsedUnit = (inventoryItem, usedUnit) => {
  if (!inventoryItem) return 0;

  if (usedUnit && inventoryItem.skus?.length) {
    const matchedSku = inventoryItem.skus.find((sku) =>
      skuMatchesUnit(sku, usedUnit)
    );
    if (matchedSku?.quantity != null) {
      return matchedSku.quantity;
    }
  }

  if (
    usedUnit &&
    inventoryItem.defaultSku &&
    skuMatchesUnit(inventoryItem.defaultSku, usedUnit) &&
    inventoryItem.defaultSku.quantity != null
  ) {
    return inventoryItem.defaultSku.quantity;
  }

  return inventoryItem.inventoryQuantity || 0;
};

/**
 * Hệ số quy đổi của usedUnit = số đơn vị nhỏ nhất (minSku) trong 1 usedUnit.
 *   - usedUnit trùng minSku          → 1
 *   - usedUnit trùng 1 SKU trong rows → coefficient của SKU đó (vd Hộp = 100)
 *   - không xác định được            → null (caller coi như minSku, ÷ 1)
 * Ghi chú: rows từ BE chỉ có skuId/skuName (không có skuCode) và skuId là id
 * bên Store nên khớp cross-system chủ yếu dựa vào TÊN đơn vị (skuMatchesUnit).
 */
const resolveUsedUnitCoefficient = (availabilityData, usedUnit) => {
  if (!usedUnit) return 1;
  if (usedUnitMatchesMinSku(availabilityData, usedUnit)) return 1;

  const rows = availabilityData?.rows || [];
  const matched = rows.find((row) => skuMatchesUnit(row, usedUnit));
  if (matched && matched.coefficient > 0) return matched.coefficient;

  return null;
};

/**
 * Tồn theo usedUnit từ POST /api/stock/availability.
 * Công thức: availableMinSku ÷ hệ số(usedUnit) → TỔNG quy ra đơn vị bán (gồm cả phần lẻ).
 *   - usedUnit = minSku (vd Viên): ÷ 1 → totalMinSku - reservedMinSku.
 *   - usedUnit lớn hơn (vd Hộp=100): 250 viên → 2.5 hộp.
 * Không xác định được hệ số (đơn vị lớn nhưng không có lô nhập/không khớp tên)
 * → coi như đơn vị nhỏ nhất (÷ 1) thay vì trả 0.
 */
export const getAvailabilityQuantityByUsedUnit = (availabilityData, usedUnit) => {
  if (!availabilityData) return 0;

  const availableMinSku = getAvailableMinSku(availabilityData);
  const coef = resolveUsedUnitCoefficient(availabilityData, usedUnit);
  const divisor = coef && coef > 0 ? coef : 1;

  return roundInventoryQty(availableMinSku / divisor);
};

/**
 * Quy đổi xuất kho theo ĐƠN VỊ NHỎ NHẤT dựa vào skus[] (POST /api/stock/availability?includeSkus=true).
 * Map đơn vị tính (usedUnit) ↔ SKU bên kho QUA MÃ (skuCode = mã đơn vị HIS).
 *
 * Trả về:
 *   {
 *     coefficient,     // trọng số: số ĐV nhỏ nhất trong 1 ĐV tính (SKU khớp usedUnit). null nếu không map được.
 *     exportUnitCode,  // mã đơn vị nhỏ nhất bên kho (minSkuCode)
 *     exportUnitName,  // tên đơn vị nhỏ nhất (minSkuName)
 *     resolved,        // true nếu map được coefficient qua mã
 *   }
 * Quy ước: đơn vị nhỏ nhất (minSku) là SKU có coefficient = 1.
 */
export const resolveExportBySku = (availabilityData, usedUnit) => {
  const skus = availabilityData?.skus || [];

  // Đơn vị nhỏ nhất: ưu tiên top-level minSkuCode/Name; fallback SKU có coefficient = 1.
  const minSku = skus.find((s) => Number(s?.coefficient) === 1);
  const exportUnitCode =
    availabilityData?.minSkuCode || minSku?.skuCode || null;
  const exportUnitName =
    availabilityData?.minSkuName || minSku?.skuName || null;

  // Khớp usedUnit với SKU qua MÃ (code). Fallback theo tên chỉ khi không có mã.
  let matched = null;
  if (usedUnit?.code) {
    const unitCode = normalizeUnitCode(usedUnit.code);
    matched = skus.find(
      (s) => s?.skuCode && normalizeUnitCode(s.skuCode) === unitCode
    );
  }
  if (!matched && usedUnit?.name) {
    const unitName = normalizeSkuName(usedUnit.name);
    matched = skus.find(
      (s) => s?.skuName && normalizeSkuName(s.skuName) === unitName
    );
  }

  const coefficient =
    matched && Number(matched.coefficient) > 0
      ? Number(matched.coefficient)
      : null;

  return {
    coefficient,
    exportUnitCode,
    exportUnitName,
    resolved: coefficient != null,
  };
};

/** Tồn khả dụng theo ĐƠN VỊ NHỎ NHẤT (minSku) = totalMinSku − reservedMinSku. */
export const getAvailabilityMinSkuQuantity = (availabilityData) =>
  roundInventoryQty(getAvailableMinSku(availabilityData));

/**
 * Quy đổi số lượng từ đơn vị nhỏ nhất → đơn vị tính = minSkuQty ÷ hệ số.
 * Dùng để hiển thị tồn tương đương theo đơn vị tính (vd 200 tuýp ÷ 2 = 100 hộp).
 */
export const getUsedUnitQuantityFromMinSku = (minSkuQty, coefficient) => {
  const q = Number(minSkuQty);
  if (!Number.isFinite(q)) return 0;
  const coef = Number(coefficient) > 0 ? Number(coefficient) : 1;
  return roundInventoryQty(q / coef);
};

/** Số lượng xuất quy về đơn vị nhỏ nhất = SL theo đơn vị tính × hệ số. */
export const getExportedMinSkuQuantity = (currentQuantity, coefficient) => {
  const qty = Number(currentQuantity) || 0;
  const coef = Number(coefficient) > 0 ? Number(coefficient) : 1;
  return roundInventoryQty(qty * coef);
};
